/**
 * useCardKeyboardShortcuts - Keyboard shortcuts for the Expanding Card
 *
 * - Escape: Close card, close edit form, or close full modal (in priority order)
 * - 1-4:   Toggle tabs (Aircraft, Financial, Client, Schedule)
 *
 * Only active when an aircraft is selected. Skipped when typing in inputs.
 */

import { useEffect } from 'react'
import { useAircraftCardStore, type TabId } from '@/stores/aircraftCardStore'
import { useAircraftEditorStore } from '@/stores/aircraftEditorStore'

const TAB_KEYS: Record<string, TabId> = {
  '1': 'aircraft',
  '2': 'financial',
  '3': 'client',
  '4': 'schedule',
}

export function useCardKeyboardShortcuts() {
  const selectedAllocationId = useAircraftEditorStore((s) => s.selectedAllocationId)

  useEffect(() => {
    if (!selectedAllocationId) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip when typing in inputs
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      const store = useAircraftCardStore.getState()

      // Escape: Close in priority order
      if (e.key === 'Escape') {
        e.preventDefault()
        if (store.editFormOpen) {
          store.closeEditForm()
        } else if (store.cardState.mode === 'fullModal') {
          store.closeFullModal()
        } else if (store.cardState.mode === 'expanded') {
          store.openCompact()
        } else if (store.cardState.mode === 'compact') {
          store.closeCard()
          useAircraftEditorStore.getState().selectAllocation(null)
        }
        return
      }

      // 1-4: Toggle tabs
      const tabId = TAB_KEYS[e.key]
      if (tabId) {
        const { cardState } = store
        // Only toggle tabs in compact or expanded modes
        if (cardState.mode === 'compact' || cardState.mode === 'expanded') {
          store.toggleTab(tabId)
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [selectedAllocationId])
}
